import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

// ---- Types ----

interface TourStep {
  title: string;
  content: string;
  selector: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  padding?: number;
}

interface TourDefinition {
  name: string;
  steps: TourStep[];
}

interface TourContextValue {
  startTour: (name: string) => void;
  closeTour: () => void;
  isActive: boolean;
}

// ---- Context ----

const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  closeTour: () => {},
  isActive: false,
});

export function useTour() {
  return useContext(TourContext);
}

// ---- Tour definitions ----

const TOURS: TourDefinition[] = [
  {
    name: 'welcome',
    steps: [
      {
        title: 'Your Dashboard',
        content: 'This is your home base. Get a quick overview of sales, orders, and key metrics for your business.',
        selector: '#tour-dashboard-stats',
        side: 'bottom',
        padding: 8,
      },
      {
        title: 'Navigation',
        content: 'Use the sidebar to move between sections. Your available features depend on your business type and plan.',
        selector: '#tour-sidebar-nav',
        side: 'right',
        padding: 4,
      },
      {
        title: 'Start Selling',
        content: 'The Sell screen is your POS terminal. Ring up transactions, apply discounts, and process payments here.',
        selector: '#tour-sell-link',
        side: 'right',
        padding: 4,
      },
      {
        title: 'Manage Your Catalog',
        content: 'Add products, set prices, organize categories, and manage your inventory from the catalog section.',
        selector: '#tour-products-link',
        side: 'right',
        padding: 4,
      },
      {
        title: 'Settings & Billing',
        content: 'Configure your locations, team, payment methods, and manage your subscription from Settings.',
        selector: '#tour-settings-link',
        side: 'right',
        padding: 4,
      },
    ],
  },
];

// ---- Spotlight + Card overlay ----

function TourOverlay({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onClose,
}: {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Find and track the target element
  useEffect(() => {
    const update = () => {
      const el = document.querySelector(step.selector);
      if (el) {
        setRect(el.getBoundingClientRect());
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step.selector]);

  if (!rect) return null;

  const pad = step.padding ?? 6;
  const spotX = rect.left - pad;
  const spotY = rect.top - pad;
  const spotW = rect.width + pad * 2;
  const spotH = rect.height + pad * 2;

  // Position card relative to spotlight
  const cardStyle: React.CSSProperties = {};
  const side = step.side || 'bottom';
  switch (side) {
    case 'bottom':
      cardStyle.top = spotY + spotH + 12;
      cardStyle.left = Math.max(8, spotX);
      break;
    case 'top':
      cardStyle.bottom = window.innerHeight - spotY + 12;
      cardStyle.left = Math.max(8, spotX);
      break;
    case 'right':
      cardStyle.top = spotY;
      cardStyle.left = spotX + spotW + 12;
      break;
    case 'left':
      cardStyle.right = window.innerWidth - spotX + 12;
      cardStyle.top = spotY;
      break;
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999]" onClick={onClose}>
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotX}
              y={spotY}
              width={spotW}
              height={spotH}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0" y="0"
          width="100%" height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Spotlight border highlight */}
      <div
        className="absolute border-2 border-orange-400 rounded-lg pointer-events-none"
        style={{
          left: spotX,
          top: spotY,
          width: spotW,
          height: spotH,
          boxShadow: '0 0 0 4px rgba(251, 146, 60, 0.2)',
        }}
      />

      {/* Card */}
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.2 }}
        className="absolute bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-700 w-[320px] overflow-hidden"
        style={cardStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-2">
          <h3 className="font-semibold text-zinc-900 dark:text-white text-sm">
            {step.title}
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 p-0.5 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-3 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
          {step.content}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 flex items-center justify-between">
          <span className="text-xs text-zinc-400">
            {currentStep + 1} of {totalSteps}
          </span>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" onClick={onPrev} className="h-8 px-3 text-xs">
                <ChevronLeft className="w-3 h-3 mr-1" />
                Back
              </Button>
            )}
            {currentStep < totalSteps - 1 ? (
              <Button size="sm" onClick={onNext} className="h-8 px-3 text-xs">
                Next
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={onClose} className="h-8 px-3 text-xs">
                Done
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// ---- Provider ----

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const startTour = useCallback((name: string) => {
    const tour = TOURS.find((t) => t.name === name);
    if (tour) {
      setActiveTour(tour);
      setCurrentStep(0);
    }
  }, []);

  const closeTour = useCallback(() => {
    setActiveTour(null);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (activeTour && currentStep < activeTour.steps.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  }, [activeTour, currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  return (
    <TourContext.Provider value={{ startTour, closeTour, isActive: !!activeTour }}>
      {children}
      <AnimatePresence>
        {activeTour && (
          <TourOverlay
            key={`tour-step-${currentStep}`}
            step={activeTour.steps[currentStep]}
            currentStep={currentStep}
            totalSteps={activeTour.steps.length}
            onNext={nextStep}
            onPrev={prevStep}
            onClose={closeTour}
          />
        )}
      </AnimatePresence>
    </TourContext.Provider>
  );
}
